import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Plus, Search, Edit3, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const KIND_META = {
  articles: { label: "Articles", singular: "Article", titleField: "title", publicPath: "/articles" },
  news: { label: "News", singular: "News item", titleField: "title", publicPath: "/news" },
  interviews: { label: "Interviews", singular: "Interview", titleField: "name", publicPath: "/interviews" },
  magazines: { label: "Magazines", singular: "Magazine", titleField: "title", publicPath: "/magazine" },
};

const StatusBadge = ({ status }) => {
  const isPublished = status === "published";
  return (
    <span className={`text-[10px] font-mono-editorial uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${
      isPublished ? "bg-ink text-paper border-ink" : "text-ink-soft border-ink-line"
    }`}>
      {status || "draft"}
    </span>
  );
};

const AdminListPage = () => {
  const { kind } = useParams();
  const navigate = useNavigate();
  const meta = KIND_META[kind];
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems(await api.adminList(kind)); } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (meta) load(); /* eslint-disable-next-line */ }, [kind]);

  const filtered = useMemo(() => {
    let out = items;
    if (filter !== "all") out = out.filter((x) => (x.status || "draft") === filter);
    if (q.trim()) {
      const lo = q.toLowerCase();
      out = out.filter((x) => JSON.stringify(x).toLowerCase().includes(lo));
    }
    return out;
  }, [items, q, filter]);

  if (!meta) return <div className="text-ink-soft">Unknown content type.</div>;

  const togglePublish = async (item) => {
    try {
      if (item.status === "published") {
        await api.adminUnpublish(kind, item.id);
        toast.success("Unpublished");
      } else {
        await api.adminPublish(kind, item.id);
        toast.success("Published — now live");
      }
      load();
    } catch { toast.error("Action failed"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item[meta.titleField]}"? This cannot be undone.`)) return;
    try { await api.adminDelete(kind, item.id); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid={`admin-list-${kind}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline overline-muted mb-3">Content</div>
          <h1 className="font-display text-ink text-4xl md:text-5xl leading-[1.05] tracking-[-0.015em]">{meta.label}</h1>
        </div>
        <Link
          to={`/admin/${kind}/new`}
          data-testid={`admin-new-${kind}`}
          className="inline-flex items-center gap-2 px-6 py-3 bg-ink text-paper text-sm font-medium rounded-full hover:bg-gold-dark transition-colors"
        >
          <Plus size={16} /> New {meta.singular}
        </Link>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            data-testid="admin-list-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${meta.label.toLowerCase()}…`}
            className="w-full pl-10 pr-4 py-2.5 bg-paper border border-ink-line rounded-full text-sm outline-none focus:border-ink"
          />
        </div>
        {["all", "published", "draft"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`admin-filter-${f}`}
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-mono-editorial border rounded-full transition-colors ${
              filter === f ? "bg-ink text-paper border-ink" : "text-ink-soft border-ink-line hover:border-ink hover:text-ink"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-6 editorial-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-ink-soft text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-ink-soft text-sm">No items match.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-cream text-left text-[11px] font-mono-editorial uppercase tracking-[0.16em] text-ink-muted">
              <tr>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Category</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Updated</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id} data-testid={`admin-row-${item.id}`} className="border-t border-ink-line hover:bg-cream/60">
                  <td className="px-5 py-3">
                    <button
                      className="font-display text-ink text-base leading-tight hover:text-gold-dark text-left"
                      onClick={() => navigate(`/admin/${kind}/edit/${item.id}`)}
                    >
                      {item[meta.titleField] || item.slug}
                    </button>
                    {kind === "interviews" && <div className="text-xs text-ink-muted mt-0.5">{item.company} · {item.position}</div>}
                  </td>
                  <td className="px-5 py-3 text-ink-soft">{item.category || item.edition || "—"}</td>
                  <td className="px-5 py-3"><StatusBadge status={item.status} /></td>
                  <td className="px-5 py-3 text-ink-muted text-xs font-mono-editorial">
                    {item.updated_at ? new Date(item.updated_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => togglePublish(item)} data-testid={`admin-toggle-${item.id}`} title={item.status === "published" ? "Unpublish" : "Publish"} className="w-8 h-8 flex items-center justify-center rounded-full text-ink-soft hover:text-ink hover:bg-cream">
                        {item.status === "published" ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                      {item.status === "published" && item.slug && kind !== "magazines" && (
                        <a href={`${meta.publicPath}/${item.slug}`} target="_blank" rel="noreferrer" title="View live" className="w-8 h-8 flex items-center justify-center rounded-full text-ink-soft hover:text-ink hover:bg-cream">
                          <ExternalLink size={14} />
                        </a>
                      )}
                      <button onClick={() => navigate(`/admin/${kind}/edit/${item.id}`)} data-testid={`admin-edit-${item.id}`} title="Edit" className="w-8 h-8 flex items-center justify-center rounded-full text-ink-soft hover:text-ink hover:bg-cream">
                        <Edit3 size={14} />
                      </button>
                      <button onClick={() => remove(item)} data-testid={`admin-delete-${item.id}`} title="Delete" className="w-8 h-8 flex items-center justify-center rounded-full text-ink-soft hover:text-red-600 hover:bg-cream">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminListPage;
